import React, { useState, useRef, useEffect, useMemo } from "react";
import QRCode from "qrcode";
import { ToolContainer, DownloadButton, downloadBlob } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { X } from "lucide-react";
import { toast } from "sonner";
import { contrastWarning, mailtoPayload, smsPayload, telPayload, vcardPayload, wifiPayload, type WifiSecurity } from "@/lib/qrHelpers";
import { NoticeBox } from "@/components/NoticeBox";

const TOOL_INFO_CONTENT = {
  what: `Generates a QR code for a web link, plain text, WiFi login, contact card (vCard), email, text message or phone number, ready to download as PNG (up to 2,000 px) or SVG for print. Optionally overlay a logo in the centre.`,
  how: `Pick what the code should do (Link, Text, WiFi, Contact, Email, SMS or Phone), fill in the fields, and the QR code appears. Choose a download size, then save it as PNG or SVG. Use SVG for posters, flyers and anything printed large.`,
  tip: `Test the QR code with your own phone's camera before using it anywhere public or printed. Keep a dark code on a light background, and leave the quiet-zone margin alone: it is what lets scanners find the code. With a logo added, error correction is raised to its strongest level automatically, but always verify a scan before printing.`,
};

// With a logo present, error correction is forced to H (recovers ~30%) and the
// logo is kept to ~20% of the width.
const LOGO_SIZE_RATIO = 0.2;
const PREVIEW_PX = 300;
const MARGIN = 4; // the QR standard's quiet zone, in modules

/** Most bytes a version-40 QR code holds, by error-correction level. */
const QR_CAPACITY: Record<"L" | "M" | "Q" | "H", number> = { L: 2953, M: 2331, Q: 1663, H: 1273 };
const byteLength = (t: string) => new TextEncoder().encode(t).length;

type Kind = "url" | "text" | "wifi" | "contact" | "email" | "sms" | "phone";
const KINDS: { value: Kind; label: string }[] = [
  { value: "url", label: "Link" },
  { value: "text", label: "Text" },
  { value: "wifi", label: "WiFi" },
  { value: "contact", label: "Contact" },
  { value: "email", label: "Email" },
  { value: "sms", label: "SMS" },
  { value: "phone", label: "Phone" },
];

export default function QrGenerator() {
  const [kind, setKind] = useState<Kind>("url");
  const [url, setUrl] = useState("https://freedailypro.com");
  const [plain, setPlain] = useState("");
  const [wifi, setWifi] = useState({ ssid: "", password: "", security: "WPA" as WifiSecurity, hidden: false });
  const [contact, setContact] = useState({ first: "", last: "", phone: "", email: "", company: "", website: "" });
  const [mail, setMail] = useState({ to: "", subject: "", body: "" });
  const [sms, setSms] = useState({ phone: "", body: "" });
  const [phone, setPhone] = useState("");

  const [size, setSize] = useState("1000");
  const [color, setColor] = useState("#000000");
  const [bgColor, setBgColor] = useState("#ffffff");
  const [ec, setEc] = useState<"L" | "M" | "Q" | "H">("M");
  const [logoData, setLogoData] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);

  const text = useMemo(() => {
    switch (kind) {
      case "url": {
        // "example.com" scans as plain text on many phones, so a bare address gets https:// in front.
        const u = url.trim();
        return u && !/^[a-z][a-z0-9+.-]*:/i.test(u) ? `https://${u}` : u;
      }
      case "text": return plain;
      case "wifi": return wifi.ssid ? wifiPayload(wifi) : "";
      case "contact": return contact.first || contact.last || contact.phone || contact.email ? vcardPayload(contact) : "";
      case "email": return mail.to.trim() ? mailtoPayload(mail) : "";
      case "sms": return sms.phone.trim() ? smsPayload(sms.phone, sms.body) : "";
      case "phone": return phone.trim() ? telPayload(phone) : "";
    }
  }, [kind, url, plain, wifi, contact, mail, sms, phone]);

  const level = logoData ? "H" : ec;
  const bytes = byteLength(text);
  const capacity = QR_CAPACITY[level];
  const tooLong = bytes > capacity;
  const warning = contrastWarning(color, bgColor);

  const handleLogoUpload = (file: File | undefined) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please upload an image file for the logo."); return; }
    const r = new FileReader();
    r.onload = () => setLogoData(String(r.result));
    r.readAsDataURL(file);
  };
  const removeLogo = () => { setLogoData(null); if (logoInputRef.current) logoInputRef.current.value = ""; };

  /** Draws the code (and logo) onto `canvas` at `width` px. */
  const draw = async (canvas: HTMLCanvasElement, width: number) => {
    await QRCode.toCanvas(canvas, text, { width, color: { dark: color, light: bgColor }, margin: MARGIN, errorCorrectionLevel: level });
    if (!logoData) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const img = new Image();
    await new Promise<void>((res, rej) => { img.onload = () => res(); img.onerror = () => rej(new Error("logo")); img.src = logoData; });
    const logoSize = canvas.width * LOGO_SIZE_RATIO;
    const x = (canvas.width - logoSize) / 2, y = (canvas.height - logoSize) / 2, pad = logoSize * 0.12;
    ctx.fillStyle = bgColor;
    ctx.fillRect(x - pad, y - pad, logoSize + pad * 2, logoSize + pad * 2);
    ctx.drawImage(img, x, y, logoSize, logoSize);
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !text || tooLong) return;
    draw(canvas, PREVIEW_PX).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, color, bgColor, level, logoData, tooLong]);

  const downloadPng = async () => {
    if (tooLong) { toast.error("Too long for a QR code at this error-correction level"); return; }
    if (!text) { toast.error("Fill in the details first"); return; }
    try {
      const c = document.createElement("canvas");
      await draw(c, Number(size));
      c.toBlob(blob => { if (blob) downloadBlob(blob, "qrcode.png"); });
    } catch { toast.error("Couldn't create the PNG"); }
  };

  const downloadSvg = async () => {
    if (tooLong) { toast.error("Too long for a QR code at this error-correction level"); return; }
    if (!text) { toast.error("Fill in the details first"); return; }
    try {
      let svg = await QRCode.toString(text, { type: "svg", margin: MARGIN, errorCorrectionLevel: level, color: { dark: color, light: bgColor } });
      if (logoData) {
        const m = /viewBox="0 0 (\d+) (\d+)"/.exec(svg);
        const n = m ? Number(m[1]) : 0;
        if (n) {
          const ls = n * LOGO_SIZE_RATIO, x = (n - ls) / 2, pad = ls * 0.12;
          const extra = `<rect x="${x - pad}" y="${x - pad}" width="${ls + pad * 2}" height="${ls + pad * 2}" fill="${bgColor}"/><image href="${logoData}" x="${x}" y="${x}" width="${ls}" height="${ls}"/>`;
          svg = svg.replace("</svg>", extra + "</svg>");
        }
      }
      downloadBlob(new Blob([svg], { type: "image/svg+xml" }), "qrcode.svg");
    } catch { toast.error("Couldn't create the SVG"); }
  };

  const f = (label: string, el: React.ReactNode) => (
    <div><Label>{label}</Label>{React.isValidElement<{ "aria-label"?: string }>(el) && (el.type === Input || el.type === Textarea) && !el.props["aria-label"] ? React.cloneElement(el, { "aria-label": label }) : el}</div>
  );

  return (
    <ToolContainer>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="space-y-4">
          <div role="tablist" aria-label="QR code type" className="flex flex-wrap gap-1.5">
            {KINDS.map(k => (
              <Button key={k.value} role="tab" aria-selected={kind === k.value} data-testid={`qr-type-${k.value}`} size="sm"
                variant={kind === k.value ? "default" : "outline"} onClick={() => setKind(k.value)}>{k.label}</Button>
            ))}
          </div>

          {kind === "url" && f("Web address", <Input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://example.com" data-testid="qr-url" />)}
          {kind === "text" && f("Text", <Textarea value={plain} onChange={e => setPlain(e.target.value)} placeholder="Any text" data-testid="qr-text" />)}
          {kind === "wifi" && (
            <div className="space-y-3">
              {f("Network name (SSID)", <Input value={wifi.ssid} onChange={e => setWifi({ ...wifi, ssid: e.target.value })} data-testid="qr-wifi-ssid" />)}
              {wifi.security !== "nopass" && f("Password", <Input value={wifi.password} onChange={e => setWifi({ ...wifi, password: e.target.value })} data-testid="qr-wifi-password" />)}
              {f("Security", (
                <Select value={wifi.security} onValueChange={v => setWifi({ ...wifi, security: v as WifiSecurity })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="WPA">WPA/WPA2/WPA3</SelectItem>
                    <SelectItem value="WEP">WEP</SelectItem>
                    <SelectItem value="nopass">None (open network)</SelectItem>
                  </SelectContent>
                </Select>
              ))}
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={wifi.hidden} onChange={e => setWifi({ ...wifi, hidden: e.target.checked })} /> Hidden network</label>
            </div>
          )}
          {kind === "contact" && (
            <div className="grid grid-cols-2 gap-3">
              {f("First name", <Input value={contact.first} onChange={e => setContact({ ...contact, first: e.target.value })} data-testid="qr-vc-first" />)}
              {f("Last name", <Input value={contact.last} onChange={e => setContact({ ...contact, last: e.target.value })} />)}
              {f("Phone", <Input type="tel" value={contact.phone} onChange={e => setContact({ ...contact, phone: e.target.value })} />)}
              {f("Email", <Input type="email" value={contact.email} onChange={e => setContact({ ...contact, email: e.target.value })} />)}
              {f("Company", <Input value={contact.company} onChange={e => setContact({ ...contact, company: e.target.value })} />)}
              {f("Website", <Input value={contact.website} onChange={e => setContact({ ...contact, website: e.target.value })} />)}
            </div>
          )}
          {kind === "email" && (
            <div className="space-y-3">
              {f("To", <Input type="email" value={mail.to} onChange={e => setMail({ ...mail, to: e.target.value })} data-testid="qr-mail-to" />)}
              {f("Subject (optional)", <Input value={mail.subject} onChange={e => setMail({ ...mail, subject: e.target.value })} />)}
              {f("Message (optional)", <Textarea value={mail.body} onChange={e => setMail({ ...mail, body: e.target.value })} />)}
            </div>
          )}
          {kind === "sms" && (
            <div className="space-y-3">
              {f("Phone number", <Input type="tel" value={sms.phone} onChange={e => setSms({ ...sms, phone: e.target.value })} data-testid="qr-sms-phone" />)}
              {f("Message (optional)", <Textarea value={sms.body} onChange={e => setSms({ ...sms, body: e.target.value })} />)}
            </div>
          )}
          {kind === "phone" && f("Phone number", <Input type="tel" value={phone} onChange={e => setPhone(e.target.value)} data-testid="qr-phone" />)}

          <div className="grid grid-cols-2 gap-4">
            {f("Download size", (
              <Select value={size} onValueChange={setSize}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["200", "300", "500", "1000", "1500", "2000"].map(s => <SelectItem key={s} value={s}>{s} px</SelectItem>)}
                </SelectContent>
              </Select>
            ))}
            {f("Error correction", (
              <Select value={level} onValueChange={v => setEc(v as "L" | "M" | "Q" | "H")} disabled={!!logoData}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="L">L (7%)</SelectItem>
                  <SelectItem value="M">M (15%)</SelectItem>
                  <SelectItem value="Q">Q (25%)</SelectItem>
                  <SelectItem value="H">H (30%)</SelectItem>
                </SelectContent>
              </Select>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-4">
            {f("Color", <Input type="color" value={color} onChange={e => setColor(e.target.value)} aria-label="Code colour" />)}
            {f("Background", <Input type="color" value={bgColor} onChange={e => setBgColor(e.target.value)} aria-label="Background colour" />)}
          </div>
          {warning && <NoticeBox className="text-sm" role="alert" testId="qr-contrast-warning">{warning}</NoticeBox>}

          <div>
            <Label>Logo (optional)</Label>
            {logoData ? (
              <div className="flex items-center gap-2 mt-1.5">
                <img src={logoData} alt="Logo preview" className="h-9 w-9 rounded object-contain border bg-white" />
                <span className="text-xs text-muted-foreground flex-1">Logo added. Error correction raised automatically to keep it scannable.</span>
                <Button size="icon" variant="ghost" onClick={removeLogo} className="h-7 w-7 shrink-0"><X className="h-3.5 w-3.5" /></Button>
              </div>
            ) : (
              <Input ref={logoInputRef} type="file" accept="image/*" onChange={e => handleLogoUpload(e.target.files?.[0])} className="mt-1.5" />
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <DownloadButton onClick={downloadPng} label="Download PNG" disabled={!text || tooLong} />
            <DownloadButton onClick={downloadSvg} label="Download SVG" disabled={!text || tooLong} />
          </div>
          {text && (
            <p role={tooLong ? "alert" : undefined} className={`text-xs ${tooLong ? "text-destructive" : "text-muted-foreground"}`} data-testid="qr-capacity">
              {tooLong
                ? `Too long for a QR code: ${bytes.toLocaleString("en-US")} bytes, and level ${level} holds at most ${capacity.toLocaleString("en-US")}. Shorten it${level !== "L" && !logoData ? " or choose error correction L for the most room" : ""}.`
                : `${bytes.toLocaleString("en-US")} of ${capacity.toLocaleString("en-US")} bytes used at level ${level}.`}
            </p>
          )}
        </div>
        <div className="flex items-center justify-center p-4 border rounded-xl bg-card min-h-[320px]">
          {text && !tooLong ? <canvas ref={canvasRef} data-testid="qr-canvas" className="max-w-full h-auto" /> : <p className="text-sm text-muted-foreground text-center">{tooLong ? "Shorten the text to see the code." : "Fill in the details and your QR code appears here."}</p>}
        </div>
      </div>
      <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
